import React from 'react';

/**
 * NetworkSpinner — ONET branded loading spinner
 *
 * Nodes are placed at organic / asymmetric positions to evoke the ONET network
 * logo (not a regular polygon). Packets travel along edges to suggest data flow.
 *
 * Props
 * ─────
 *  size      number   (default 40)   width & height in px
 *  color     string   (default '#7c3aed')  primary violet color
 *  light     bool     (default false) use a lighter palette (for dark buttons)
 *  className string   extra className on wrapper <span>
 */
const NetworkSpinner = ({ size = 40, color = '#7c3aed', light = false, className = '' }) => {
    // unique id prefix so multiple instances don't clash
    const uid = React.useId().replace(/:/g, '');

    const nodeColor = light ? '#ede9fe' : '#c4b5fd';
    const strokeColor = light ? '#ddd6fe' : color;
    const glowColor = light ? '#ede9fe' : '#c4b5fd';

    // Node positions on 100×100 viewBox (deliberately asymmetric)
    // format: [cx, cy, r]
    const outer = [
        [24, 16, 3.5],   // top-left
        [68, 11, 3.0],   // top-right (closer to top)
        [88, 36, 4.0],   // right-high
        [82, 70, 3.5],   // right-low
        [48, 90, 3.0],   // bottom
        [14, 66, 4.0],   // left-low
        [10, 32, 3.0],   // left-high
    ];
    const inner = [
        [40, 30, 2.5],   // inner top-left
        [70, 55, 2.5],   // inner right
        [35, 70, 2.5],   // inner bottom-left
    ];

    // Edges to render
    const hubEdges = [
        [50, 50, 24, 16], [50, 50, 88, 36], [50, 50, 82, 70],
        [50, 50, 14, 66], [50, 50, 10, 32],
        [50, 50, 40, 30], [50, 50, 70, 55], [50, 50, 35, 70],
    ];
    const ringEdges = [
        [24, 16, 68, 11], [68, 11, 88, 36], [88, 36, 82, 70],
        [82, 70, 48, 90], [48, 90, 14, 66], [14, 66, 10, 32], [10, 32, 24, 16],
    ];
    const crossEdges = [
        [24, 16, 40, 30], [88, 36, 70, 55], [48, 90, 35, 70],
        [14, 66, 35, 70], [68, 11, 70, 55],
    ];

    // Packet paths [x1,y1 → x2,y2], duration, begin
    const packets = [
        { d: `M50,50 L24,16`, dur: '1.5s', begin: '0s' },
        { d: `M50,50 L82,70`, dur: '1.9s', begin: '.7s' },
        { d: `M50,50 L14,66`, dur: '1.7s', begin: '1.3s' },
        { d: `M50,50 L88,36`, dur: '1.6s', begin: '.2s' },
        { d: `M24,16 L88,36`, dur: '1.2s', begin: '.4s' },
        { d: `M88,36 L48,90`, dur: '1.4s', begin: '1.0s' },
    ];

    const filterId = `onet-glow-${uid}`;
    const nodeGradId = `onet-node-${uid}`;
    const packetGradId = `onet-packet-${uid}`;

    return (
        <span
            className={className}
            style={{ display: 'inline-flex', width: size, height: size, flexShrink: 0 }}
            role="status"
            aria-label="Caricamento..."
        >
            <svg
                viewBox="-2 -2 104 104"
                width={size}
                height={size}
                xmlns="http://www.w3.org/2000/svg"
                style={{ overflow: 'visible' }}
            >
                <defs>
                    <filter id={filterId} x="-40%" y="-40%" width="180%" height="180%">
                        <feGaussianBlur stdDeviation="2.2" result="b" />
                        <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
                    </filter>
                    <radialGradient id={nodeGradId} cx="35%" cy="35%" r="65%">
                        <stop offset="0%" stopColor={nodeColor} />
                        <stop offset="100%" stopColor={color} />
                    </radialGradient>
                    <radialGradient id={packetGradId} cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#ffffff" stopOpacity=".95" />
                        <stop offset="100%" stopColor={glowColor} stopOpacity=".7" />
                    </radialGradient>
                    <style>{`
            @keyframes onet-np { 0%,100%{opacity:.2} 50%{opacity:1} }
            @keyframes onet-hp { 0%,100%{opacity:.6} 50%{opacity:1} }
            @keyframes onet-ef { 0%,100%{stroke-opacity:.08} 50%{stroke-opacity:.6} }
            .${uid}-np { animation: onet-np 2s ease-in-out infinite; }
            .${uid}-hp { animation: onet-hp 1.8s ease-in-out infinite; }
            .${uid}-ef { animation: onet-ef 2.5s ease-in-out infinite; }
          `}</style>
                </defs>

                {/* Outer sphere hint */}
                <circle cx="50" cy="50" r="46" fill="none"
                    stroke={strokeColor} strokeWidth=".5" strokeOpacity=".18" />

                {/* Hub edges */}
                {hubEdges.map(([x1, y1, x2, y2], i) => (
                    <line key={`h${i}`} className={`${uid}-ef`}
                        x1={x1} y1={y1} x2={x2} y2={y2}
                        stroke={strokeColor} strokeWidth=".9" fill="none"
                        style={{ animationDelay: `${i * 0.15}s` }} />
                ))}
                {/* Ring edges */}
                {ringEdges.map(([x1, y1, x2, y2], i) => (
                    <line key={`r${i}`} className={`${uid}-ef`}
                        x1={x1} y1={y1} x2={x2} y2={y2}
                        stroke={strokeColor} strokeWidth=".7" fill="none"
                        style={{ animationDelay: `${0.1 + i * 0.15}s` }} />
                ))}
                {/* Cross edges */}
                {crossEdges.map(([x1, y1, x2, y2], i) => (
                    <line key={`c${i}`} className={`${uid}-ef`}
                        x1={x1} y1={y1} x2={x2} y2={y2}
                        stroke={strokeColor} strokeWidth=".6" fill="none"
                        style={{ animationDelay: `${0.2 + i * 0.2}s` }} />
                ))}

                {/* Nodes */}
                <g filter={`url(#${filterId})`} fill={`url(#${nodeGradId})`}>
                    {outer.map(([cx, cy, r], i) => (
                        <circle key={`o${i}`} className={`${uid}-np`}
                            cx={cx} cy={cy} r={r}
                            style={{ animationDelay: `${i * 0.2}s` }} />
                    ))}
                    {inner.map(([cx, cy, r], i) => (
                        <circle key={`i${i}`} className={`${uid}-np`}
                            cx={cx} cy={cy} r={r}
                            style={{ animationDelay: `${0.1 + i * 0.4}s` }} />
                    ))}
                    {/* Hub */}
                    <circle className={`${uid}-hp`} cx="50" cy="50" r="5.5" />
                </g>

                {/* Packets */}
                {packets.map((p, i) => {
                    const pathId = `${uid}-pkt-${i}`;
                    return (
                        <React.Fragment key={pathId}>
                            <path id={pathId} d={p.d} fill="none" />
                            <circle r={size < 28 ? 1.6 : 2.1}
                                fill={`url(#${packetGradId})`}
                                filter={`url(#${filterId})`}>
                                <animateMotion dur={p.dur} repeatCount="indefinite" begin={p.begin}>
                                    <mpath href={`#${pathId}`} />
                                </animateMotion>
                            </circle>
                        </React.Fragment>
                    );
                })}
            </svg>
        </span>
    );
};

export default NetworkSpinner;
